/**
 * What blocks hold.
 *
 * The first state a block carries that will not fit in its id. Doors, stoves
 * and beds encode everything they need - which way they face, whether they are
 * open - into a handful of packed ids, because there are only ever a few
 * possibilities. A скриня holds twenty-seven stacks of anything, so it needs
 * storage beside the voxel grid rather than inside it.
 *
 * Keyed by position. When the block goes, the contents must go with it, or a
 * chest broken and replaced in the same spot would hand the player back
 * somebody else's things - see World.setBlock, which is the single place that
 * can notice a chest stopping being a chest.
 */

import { Inventory } from "../inventory/inventory.js";
import type { ItemStack } from "../inventory/inventory.js";

/** Three rows of nine, the same width as the bag it is emptied into. */
export const CHEST_SLOTS = 27;

/** One chest, flattened for the save file. */
export interface StoredContainer {
  x: number;
  y: number;
  z: number;
  slots: (ItemStack | null)[];
}

function key(x: number, y: number, z: number): string {
  return `${x},${y},${z}`;
}

export class Containers {
  private readonly cells = new Map<string, Inventory>();
  /**
   * Cells that must be written to the save even when they are empty.
   *
   * A village скриня is stocked out of the seed when its column grows, and
   * «Пригода» throws away country nobody is near and grows it again. So
   * "has this one been filled already" has to be a fact about the world and
   * not about whether there is anything in it - otherwise emptying a chest,
   * walking away and coming back refills it, and doing that in a circle is
   * a bread machine.
   *
   * In memory the answer is "there is an entry here". Across a save it is
   * this: an empty chest is not worth writing unless somebody has taken
   * something out of it, and then it is worth exactly one row.
   */
  private readonly kept = new Set<string>();

  /** How many chests hold anything at all. Cheap enough to ask every edit. */
  get size(): number {
    return this.cells.size;
  }

  /** What is at this cell, or null if nothing was ever put there. */
  at(x: number, y: number, z: number): Inventory | null {
    return this.cells.get(key(x, y, z)) ?? null;
  }

  /**
   * The inventory for this cell, made if it does not exist.
   *
   * Chests are created empty and lazily: placing one writes a block and
   * nothing else, so a hundred empty chests cost nothing to save.
   */
  open(x: number, y: number, z: number): Inventory {
    const k = key(x, y, z);
    const found = this.cells.get(k);
    if (found !== undefined) return found;
    const made = new Inventory(CHEST_SLOTS);
    this.cells.set(k, made);
    return made;
  }

  /**
   * This cell has had its one filling and is never to have another.
   *
   * Called where a chest is stocked from the seed rather than by a player.
   */
  markStocked(x: number, y: number, z: number): void {
    this.open(x, y, z);
    this.kept.add(key(x, y, z));
  }

  /** Take the contents away, as when the chest is broken. */
  clear(x: number, y: number, z: number): Inventory | null {
    const k = key(x, y, z);
    const found = this.cells.get(k) ?? null;
    this.cells.delete(k);
    // Broken and put back is a new chest, and a new chest may be stocked.
    this.kept.delete(k);
    return found;
  }

  /** Everything worth writing to the save. Empty chests are not. */
  save(): StoredContainer[] {
    const out: StoredContainer[] = [];
    for (const [k, inventory] of this.cells) {
      if (inventory.slots.every((s) => s === null) && !this.kept.has(k)) {
        continue;
      }
      const [x, y, z] = k.split(",").map(Number) as [number, number, number];
      out.push({ x, y, z, slots: inventory.slots.map((s) => (s === null ? null : { ...s })) });
    }
    return out;
  }

  load(list: readonly StoredContainer[]): void {
    this.cells.clear();
    this.kept.clear();
    for (const stored of list) {
      const inventory = this.open(stored.x, stored.y, stored.z);
      // An empty row in a save file is a chest somebody emptied: nothing
      // else is written empty. Keep it that way.
      if (stored.slots.every((stack) => stack === null)) {
        this.kept.add(key(stored.x, stored.y, stored.z));
      }
      stored.slots.forEach((stack, i) => {
        if (i < CHEST_SLOTS) inventory.set(i, stack === null ? null : { ...stack });
      });
    }
  }
}
